import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const AddNoteModal = ({ open, onOpenChange, project, noteForm, setNoteForm, handleCreateNote, submitting }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#1A1A1A] border-[#262626] max-w-lg" aria-describedby="add-note-description">
      <DialogHeader>
        <DialogTitle className="text-white">Add Project Note</DialogTitle>
        <p id="add-note-description" className="text-sm text-gray-500">Create meeting minutes, site visit notes, or revision remarks</p>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label className="text-gray-400">Note Type</Label>
          <Select value={noteForm.note_type} onValueChange={(value) => setNoteForm({...noteForm, note_type: value})}>
            <SelectTrigger className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#262626]">
              <SelectItem value="meeting_minutes">Meeting Minutes</SelectItem>
              <SelectItem value="site_visit_notes">Site Visit Notes</SelectItem>
              <SelectItem value="revision_remark">Revision Notes</SelectItem>
              <SelectItem value="general_note">General Note</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-gray-400">Title *</Label>
          <Input
            value={noteForm.title}
            onChange={(e) => setNoteForm({...noteForm, title: e.target.value})}
            placeholder="e.g., Design Review Meeting - Phase 1"
            className="bg-[#171717] border-[#262626]"
          />
        </div>
        <div>
          <Label className="text-gray-400">Reference (Optional)</Label>
          <Input
            value={noteForm.reference}
            onChange={(e) => setNoteForm({...noteForm, reference: e.target.value})}
            placeholder="e.g., Meeting ID, Visit Date"
            className="bg-[#171717] border-[#262626]"
          />
        </div>
        <div>
          <Label className="text-gray-400">Content *</Label>
          <Textarea
            value={noteForm.content}
            onChange={(e) => setNoteForm({...noteForm, content: e.target.value})}
            placeholder="Enter your notes here..."
            className="bg-[#171717] border-[#262626] min-h-[150px]"
          />
        </div>

        <div className="border-t border-[#262626] pt-4">
          <p className="text-sm text-gray-400 mb-3">Email Notifications</p>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-[#171717] rounded border border-[#262626]">
              <div>
                <p className="text-sm text-white">Notify Client</p>
                <p className="text-xs text-gray-500">{project.client?.email || "No email"}</p>
              </div>
              <Switch
                checked={noteForm.notify_client}
                onCheckedChange={(checked) => setNoteForm({...noteForm, notify_client: checked})}
                disabled={!project.client?.email}
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-[#171717] rounded border border-[#262626]">
              <div>
                <p className="text-sm text-white">Notify Manager</p>
                <p className="text-xs text-gray-500">Associate Manager</p>
              </div>
              <Switch
                checked={noteForm.notify_manager}
                onCheckedChange={(checked) => setNoteForm({...noteForm, notify_manager: checked})}
              />
            </div>
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-[#262626]">Cancel</Button>
        <Button onClick={handleCreateNote} disabled={submitting} className="bg-[#FF4500] hover:bg-[#E65100]">
          {submitting ? "Creating..." : "Create Note"}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const SendSummaryModal = ({ open, onOpenChange, project, summaryEvent, summaryForm, setSummaryForm, handleSendSummary, submitting }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#1A1A1A] border-[#262626] max-w-2xl max-h-[90dvh] overflow-y-auto" aria-describedby="send-summary-description">
      <DialogHeader>
        <DialogTitle className="text-white flex items-center gap-2">
          <Send className="w-5 h-5 text-[#10B981]" />
          Send {summaryEvent?.event_type === 'meeting' ? 'Meeting' : 'Site Visit'} Summary
        </DialogTitle>
        <p id="send-summary-description" className="text-sm text-gray-500">
          Share a summary of the discussion with your client as proof of what was discussed
        </p>
      </DialogHeader>

      {summaryEvent && (
        <div className="space-y-4">
          {/* Event Info */}
          <div className="p-3 bg-[#171717] rounded border border-[#262626]">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-gray-500">Date:</span> <span className="text-white">{summaryEvent.scheduled_date}</span></div>
              <div><span className="text-gray-500">Time:</span> <span className="text-white">{summaryEvent.scheduled_time}</span></div>
              {summaryEvent.agenda && <div className="col-span-2"><span className="text-gray-500">Agenda:</span> <span className="text-white">{summaryEvent.agenda}</span></div>}
              {summaryEvent.purpose && <div className="col-span-2"><span className="text-gray-500">Purpose:</span> <span className="text-white">{summaryEvent.purpose}</span></div>}
            </div>
          </div>

          <div>
            <Label className="text-gray-400">Discussion Summary *</Label>
            <Textarea
              value={summaryForm.summary}
              onChange={(e) => setSummaryForm({...summaryForm, summary: e.target.value})}
              placeholder="Summarize what was discussed during the meeting/visit..."
              className="bg-[#171717] border-[#262626] min-h-[120px]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-400">Attendees</Label>
              <Input
                value={summaryForm.attendees}
                onChange={(e) => setSummaryForm({...summaryForm, attendees: e.target.value})}
                placeholder="e.g., Ar. Jerin, Client Name"
                className="bg-[#171717] border-[#262626]"
              />
              <p className="text-xs text-gray-600 mt-1">Comma separated</p>
            </div>
            <div>
              <Label className="text-gray-400">Duration</Label>
              <Input
                value={summaryForm.duration}
                onChange={(e) => setSummaryForm({...summaryForm, duration: e.target.value})}
                placeholder="e.g., 1 hour 30 mins"
                className="bg-[#171717] border-[#262626]"
              />
            </div>
          </div>

          <div>
            <Label className="text-gray-400">Key Decisions Made</Label>
            <Textarea
              value={summaryForm.decisions}
              onChange={(e) => setSummaryForm({...summaryForm, decisions: e.target.value})}
              placeholder="List any decisions that were agreed upon..."
              className="bg-[#171717] border-[#262626] min-h-[80px]"
            />
          </div>

          <div>
            <Label className="text-gray-400">Action Items</Label>
            <Textarea
              value={summaryForm.action_items}
              onChange={(e) => setSummaryForm({...summaryForm, action_items: e.target.value})}
              placeholder="Enter action items (one per line)&#10;- Finalize floor plan by Friday&#10;- Client to confirm tile selection"
              className="bg-[#171717] border-[#262626] min-h-[80px]"
            />
            <p className="text-xs text-gray-600 mt-1">One item per line</p>
          </div>

          <div>
            <Label className="text-gray-400">Next Steps</Label>
            <Input
              value={summaryForm.next_steps}
              onChange={(e) => setSummaryForm({...summaryForm, next_steps: e.target.value})}
              placeholder="e.g., Schedule follow-up meeting next week"
              className="bg-[#171717] border-[#262626]"
            />
          </div>

          <div className="border-t border-[#262626] pt-4">
            <p className="text-sm text-gray-400 mb-3">Send Summary To</p>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-[#171717] rounded border border-[#262626]">
                <div>
                  <p className="text-sm text-white">Send to Client</p>
                  <p className="text-xs text-gray-500">{project.client?.email || "No email"}</p>
                </div>
                <Switch
                  checked={summaryForm.send_to_client}
                  onCheckedChange={(checked) => setSummaryForm({...summaryForm, send_to_client: checked})}
                  disabled={!project.client?.email}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-[#171717] rounded border border-[#262626]">
                <div>
                  <p className="text-sm text-white">Send to Manager</p>
                  <p className="text-xs text-gray-500">Associate Manager</p>
                </div>
                <Switch
                  checked={summaryForm.send_to_manager}
                  onCheckedChange={(checked) => setSummaryForm({...summaryForm, send_to_manager: checked})}
                />
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500 bg-[#171717] p-3 rounded">
            This summary will also be saved as a project note for your records.
          </p>
        </div>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-[#262626]">Cancel</Button>
        <Button onClick={handleSendSummary} disabled={submitting} className="bg-[#10B981] hover:bg-[#059669]">
          {submitting ? "Sending..." : "Send Summary"}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
